import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

import { API, Auth } from 'aws-amplify';
import * as Queries from '@/graphql/queries';
import * as Subscriptions from '@/graphql/subscriptions';

import { emptyUser, apiGetUser } from '@/users.js';

const R = require( 'ramda' );

const {
    append,
    concat,
    curry,
    eqProps,
    findIndex,
    innerJoin,
    map,
    prop,
    propEq,
    remove,
    update,
} = R;


// eslint
Queries;
Subscriptions;
append;
prop;
Auth;
API;


const initialState = () => ({

    /**
     * The current Cognito authenticated user.
     */
    currentAuthenticatedUser : null,

    /**
     * The User from Users table that corresponds to the currentAuthenticatedUser.
     */
    currentUser : emptyUser(),

    /**
     * {Array} of User objects from our schema.
     */
    users : [],

    /**
     * When false, we have not yet received all the users.
     */
    completeUsers : false,

    tndAccounts : [],

    completeTndAccounts : false,
});

const fixUserTndAccountLoginIds = user => {
    user.tndAccountLoginIds = user.tndAccountLoginIds || [];

    return user;
};

const mutations = {

    setCurrentAuthenticatedUser( state, user ) {
        state.currentAuthenticatedUser = fixUserTndAccountLoginIds( user );
    },

    setCurrentUser( state, user ) {
        state.currentUser = fixUserTndAccountLoginIds( user );
    },

    setUsers( state, { items, complete } ) {
        state.completeUsers = complete;
        state.users = map( fixUserTndAccountLoginIds, items );
    },

    /**
     * Append users. WARNING: do not add duplicate users.
     * @param {Array} users
     */
    addUsers( state, { items, complete } ) {
        state.completeUsers = complete;
        state.users = concat( state.users, map( fixUserTndAccountLoginIds, items ) );
    },

    setTndAccounts( state, { items, complete } ) {
        state.completeTndAccounts = complete;
        state.tndAccounts = items;
    },

    addTndAccounts( state, { items, complete } ) {
        state.completeTndAccounts = complete;
        state.tndAccounts = concat( state.tndAccounts, items );
    },

    updateModelList( state, { stateKey, modelKey, updatedModel, eventType } ) {
        const existingIndex = findIndex( eqProps( modelKey, updatedModel ), state[stateKey] );
        if ( eventType === 'create' || eventType === 'update' ) {
            if ( existingIndex !== -1 )
                state[stateKey] = update( existingIndex, updatedModel, state[stateKey] );
            else
                state[stateKey] = append( updatedModel, state[stateKey] );
        }
        else if ( eventType === 'delete' && existingIndex !== -1 ) {
            state[stateKey] = remove( existingIndex, 1, state[stateKey] );
        }
        else {
            throw `Invalid event type '${eventType}', expected 'create', 'upadte' or 'delete'`;
        }
    },
};

const getters = {

    /**
     * @return the current authenticated cognitor user
     */
    getCurrentAuthenticatedUser : prop( 'currentAuthenticatedUser' ),

    /**
     * @return the current User
     */
    getCurrentUser : prop( 'currentUser' ),

    /**
     * @param state
     */
    getUsers : prop( 'users' ),

    hasCompleteUsers : prop( 'completeUsers' ),

    /**
     * Return all the T&D accounts in the system.
     *
     * @param state
     */
    getTndAccounts : prop( 'tndAccounts' ),

    hasCompleteTndAccounts : prop( 'completeTndAccounts' ),

    /**
     * Return all the T&D accounts for which the currentAuthenticatedUser has access to.
     *
     * @param state
     */
    getUserTndAccounts( state ) {
        return innerJoin( ( account, loginId ) => propEq( 'loginId', loginId, account ),
                          state.tndAccounts,
                          state.currentUser.tndAccountLoginIds || [] );
    }, // getUserTndAccounts
};


const mutate = curry( ( commit, mutation, payload ) => commit( mutation, payload ) );

const apiQuery = async ( stateGuard, query, setMutation, addMutation ) => {
    if ( ! stateGuard() )
        return;

    let nextToken = null;
    do {
        const params = {
            query     : Queries[query],
            variables : { nextToken },
        };
        const result = await API.graphql( params );
        const resultNextToken = result.data[query].nextToken;

        if ( nextToken )
            addMutation( { items : result.data[query].items, complete : ! resultNextToken } );
        else
            setMutation( { items : result.data[query].items, complete : ! resultNextToken } );

        nextToken = resultNextToken;
    } while( nextToken );
};

const apiSubscriptions = {
};

/**
 * @param commit
 * @param subscriptionKey an unique ID for the subscription, for example, onCreateUser
 * @param stateKey key in the state, for example, 'users' for state.users
 * @param modelKey the model key to pass to updateModelList()
 * @param eventType the type of event: one of 'create', 'update', 'delete'
 */
const apiSubscribe = (commit, subscriptionKey, stateKey, modelKey, eventType ) => {
    const subscriptionId = stateKey + '-' + subscriptionKey;
    if ( apiSubscriptions[subscriptionId] )
        return;

    console.log( `Adding subscription ${subscriptionId}` );

    const onSubscription = API.graphql( { query : Subscriptions[subscriptionKey] } ).subscribe( {
        next : eventData => commit( {
            type: 'updateModelList',
            stateKey,
            modelKey,
            updatedModel : eventData.value.data[subscriptionKey],
            eventType,
        } ),
    } );

    apiSubscriptions[subscriptionId] = onSubscription;
};


const actions = {

    async fetchCurrentAuthenticatedUser( { commit } ) {
        const cognitoUser = await Auth.currentAuthenticatedUser();

        mutate( commit, 'setCurrentAuthenticatedUser', cognitoUser );

        const currentUser = await apiGetUser( cognitoUser.username );

        mutate( commit, 'setCurrentUser', currentUser );

    }, // fetchCurrentAuthenticatedUser

    async apiListUsers( { state, commit } ) {
        await apiQuery(
            () => state.users.length < 1,
            'listUsers',
            mutate( commit, 'setUsers' ),
            mutate( commit, 'addUsers' )
        );
    }, // apiListUsers

    apiSubscribeToUserEvents( { commit } ) {
        apiSubscribe( commit, 'onCreateUser', 'users', 'username', 'create' );
        apiSubscribe( commit, 'onUpdateUser', 'users', 'username', 'update' );
        apiSubscribe( commit, 'onDeleteUser', 'users', 'username', 'delete' );
    }, // apiSubscribeToUserEvents

    async apiListTndAccounts( { state, commit } ) {
        await apiQuery(
            () => state.tndAccounts.length < 1,
            'listTndAccounts',
            mutate( commit, 'setTndAccounts' ),
            mutate( commit, 'addTndAccounts' )
        );
    }, // apiListTndAccounts

    apiSubscribeToTndAccountEvents( { commit } ) {
        apiSubscribe( commit, 'onCreateTndAccount', 'tndAccounts', 'loginId', 'create' );
        apiSubscribe( commit, 'onUpdateTndAccount', 'tndAccounts', 'loginId', 'update' );
        apiSubscribe( commit, 'onDeleteTndAccount', 'tndAccounts', 'loginId', 'delete' );
    }, // apiSubscribeToUserEvents
};

const modules = {
};

const debug = process.env.NODE_ENV !== 'production';

export default new Vuex.Store({
    state : initialState(),
    getters,
    mutations,
    actions,
    modules,
    strict: debug,
    plugins: debug ? [Vuex.createLogger()] : [],
});
